import { iconButtonClasses, stackClasses, tablePaginationClasses } from "@mui/material";
import { applyTokens } from "../../../utilities/applyTokens.js";
import { createLazyIcon } from "../../../utilities/createLazyIcon.js";
import { classes } from "./dataGrid.classes.js";
const applyForcedHeight = (height) => ({
    height: `${height} !important`,
    minHeight: `${height} !important`,
    maxHeight: `${height} !important`,
});
const [cell, cellCheckbox, columnHeaders, columnHeader, columnHeaderCheckbox, columnHeaderDraggableContainer, columnHeaderTitleContainer, columnHeadersInner, columnsPanel, menuIcon, virtualScroller, pinnedHeadersLeft, pinnedHeadersRight, densityCompact, densityStandard, densityComfortable, resizable,] = classes;
const { root: iconButtonRoot } = iconButtonClasses;
const { root: tablePaginationRoot, toolbar, select } = tablePaginationClasses;
const applySize = (tokens, size) => {
    const { table } = tokens.component;
    const sizeTokens = table[size];
    return {
        [`& .${columnHeaders}, & .${columnHeader}`]: Object.assign({}, applyForcedHeight(sizeTokens.header.height.value)),
        [`& .${columnHeader}, & .${cell}`]: {
            paddingLeft: sizeTokens.cell.paddingLeft.value,
            paddingRight: sizeTokens.cell.paddingRight.value,
        },
        [`& .${virtualScroller}`]: {
            marginTop: `${sizeTokens.header.height.value} !important`,
        },
    };
};
const applyCheckboxCellWidth = (tokens, size = "medium") => {
    const { table, checkbox: { width }, } = tokens.component;
    const { cell: { paddingLeft, paddingRight }, } = table[size];
    const widthString = `calc(${width.value} + ${paddingLeft.value} + ${paddingRight.value}) !important;`;
    return {
        width: widthString,
        minWidth: widthString,
        maxWidth: widthString,
    };
};
const dataGrid = (tokens) => {
    const { core: { spacing }, semantic, component: { checkbox, table: { small, medium, large, header, cell, row, columnSeparator }, }, } = tokens;
    return {
        MuiDataGrid: {
            defaultProps: {
                headerHeight: parseInt(medium.header.height.value),
                getRowHeight({ densityFactor }) {
                    // densityFactor is 0.7 for compact, 1 for standard, 1.3 for comfortable
                    if (densityFactor === 0.7) {
                        return parseInt(small.cell.height.value);
                    }
                    else if (densityFactor === 1) {
                        return parseInt(medium.cell.height.value);
                    }
                    else if (densityFactor === 1.3) {
                        return parseInt(large.cell.height.value);
                    }
                    return null;
                },
                components: {
                    ColumnSortedAscendingIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/SortAscending.js")),
                    ColumnSortedDescendingIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/SortDescending.js")),
                    ColumnUnsortedIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandList.js")),
                    ColumnMenuIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/More.js")),
                    ColumnFilteredIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js")),
                    DensityCompactIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/CompactDensity.js")),
                    DensityComfortableIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/DefaultDensity.js")),
                    DensityStandardIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDensity.js")),
                    OpenFilterButtonIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js")),
                    ColumnSelectorIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Columns.js")),
                    ExportIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Export.js")),
                    QuickFilterIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Search.js")),
                    FilterPanelDeleteIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Clear.js")),
                    QuickFilterClearIcon: createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Clear.js")),
                },
                componentsProps: {
                    baseButton: {
                        size: "medium",
                    },
                    baseFormControl: {
                        variant: "outlined",
                    },
                    baseSelect: {
                        variant: "outlined",
                    },
                    baseTextField: {
                        variant: "outlined",
                        sx: {
                            paddingBottom: spacing[0].value,
                        },
                    },
                    columnsPanel: {
                        padding: spacing[0].value,
                    },
                },
            },
            styleOverrides: {
                root: {
                    border: "none",
                    gap: spacing[3].value,
                    [`&.${densityCompact}`]: applySize(tokens, "small"),
                    [`&.${densityStandard}`]: applySize(tokens, "medium"),
                    [`&.${densityComfortable}`]: applySize(tokens, "large"),
                },
                cell: Object.assign(Object.assign({}, applyTokens(cell)), { "&:focus-within, &:focus": {
                        outline: "none",
                        boxShadow: semantic.shadow.focusInset.value.stringValue,
                    }, [`&.${cellCheckbox}`]: applyCheckboxCellWidth(tokens, "medium") }),
                columnHeader: {
                    "&:focus-within, &:focus": {
                        // We want to use a box shadow instead of an outline, so that
                        // we can use border radius on the first and last column headers
                        outline: "none",
                        boxShadow: semantic.shadow.focusInset.value.stringValue,
                    },
                    "&:first-of-type": applyTokens(header.first),
                    "&:last-of-type": applyTokens(header.last),
                    "&:hover": Object.assign(Object.assign({}, applyTokens(header.hover)), { [`& .${resizable}`]: applyTokens(columnSeparator) }),
                    [`&.${columnHeaderCheckbox}`]: Object.assign(Object.assign({}, applyCheckboxCellWidth(tokens)), { [`& .${columnHeaderDraggableContainer}`]: {
                            width: checkbox.width.value,
                        }, [`& .${columnHeaderTitleContainer}`]: {
                            padding: spacing[0].value,
                        } }),
                },
                "columnHeader--alignLeft": {
                    [`& .${menuIcon}`]: {
                        marginRight: spacing[0].value,
                    },
                },
                "columnHeader--alignRight": {
                    [`& .${menuIcon}`]: {
                        marginLeft: spacing[0].value,
                    },
                },
                columnHeaders: Object.assign(Object.assign({}, applyTokens(header)), { [`&:has(.${columnHeadersInner} + .${pinnedHeadersRight}) .${columnHeadersInner} .${columnHeader}:last-of-type`]: {
                        borderTopRightRadius: spacing[0].value,
                    } }),
                columnHeaderTitleContainer: {
                    gap: spacing[1].value,
                    paddingRight: spacing["0_5"].value,
                },
                columnSeparator: {
                    color: "transparent",
                },
                columnsPanel: {
                    display: "flex",
                    flexDirection: "column",
                    gap: spacing["0_5"].value,
                },
                columnsPanelRow: {
                    padding: spacing[0].value,
                },
                filterForm: {
                    padding: spacing[0].value,
                    gap: spacing[2].value,
                },
                footerContainer: {
                    borderTopWidth: spacing[0].value,
                    [`& .${tablePaginationRoot}`]: {
                        marginTop: spacing[0].value,
                        flexGrow: 1,
                    },
                    "& > div:empty": {
                        display: "none",
                    },
                    [`& .${toolbar}`]: {
                        [`& .${select} + .${stackClasses.root}.AtlasTablePaginationActions`]: {
                            flexGrow: 1,
                        },
                    },
                },
                iconButtonContainer: {
                    [`& .${iconButtonRoot}`]: {
                        backgroundColor: "transparent",
                    },
                },
                menuIcon: {
                    marginRight: spacing[0].value,
                },
                menuIconButton: {
                    backgroundColor: "transparent",
                },
                panelContent: {
                    padding: spacing[1].value,
                    gap: spacing[2].value,
                    overflow: "unset",
                    [`& > .${columnsPanel}`]: {
                        padding: 0,
                    },
                },
                panelWrapper: {
                    padding: spacing[1].value,
                },
                pinnedColumnHeaders: {
                    backgroundColor: header.backgroundColor.value,
                    [`&.${pinnedHeadersLeft}`]: {
                        [`& .${columnHeader}:last-of-type`]: {
                            borderTopRightRadius: spacing[0].value,
                        },
                        [`& + .${columnHeadersInner} .${columnHeader}:first-of-type`]: {
                            borderTopLeftRadius: spacing[0].value,
                        },
                    },
                    [`&.${pinnedHeadersRight} .${columnHeader}:first-of-type`]: {
                        borderTopLeftRadius: spacing[0].value,
                    },
                },
                row: Object.assign(Object.assign({}, applyTokens(row.default)), { "&:hover": applyTokens(row.hover), "&.Mui-selected": applyTokens(row.selected) }),
                selectedRowCount: {
                    display: "none",
                },
                toolbarContainer: {
                    alignItems: "flex-end",
                    padding: spacing[0].value,
                    gap: spacing[1].value,
                },
            },
        },
    };
};
export default dataGrid;
//# sourceMappingURL=dataGrid.js.map